'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type Idea = {
  id: string;
  name: string;
  impact: number;
  confidence: number;
  ease: number;
};

const STORAGE_KEY = 'planetroadmap.ice-calculator.v1';

function newIdea(name = ''): Idea {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    impact: 7,
    confidence: 8,
    ease: 6,
  };
}

function score(i: Idea): number {
  return i.impact * i.confidence * i.ease;
}

function ScoreBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={value}
        onChange={() => {}}
        className="hidden"
        aria-label={label}
      />
      <span className="font-mono w-6 text-right">{value}</span>
    </div>
  );
}

export function IceCalculator() {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Idea[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setIdeas(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setIdeas([
      { ...newIdea('Onboarding redesign'), impact: 8, confidence: 7, ease: 5 },
      { ...newIdea('Pricing page A/B test'), impact: 6, confidence: 9, ease: 9 },
      { ...newIdea('AI-powered search'), impact: 9, confidence: 4, ease: 3 },
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ideas));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [ideas, hydrated]);

  const sorted = useMemo(
    () => [...ideas].map((i) => ({ ...i, score: score(i) })).sort((a, b) => b.score - a.score),
    [ideas],
  );

  function update(id: string, patch: Partial<Idea>) {
    setIdeas((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  function remove(id: string) {
    setIdeas((curr) => curr.filter((i) => i.id !== id));
  }

  function add() {
    setIdeas((curr) => [...curr, newIdea()]);
  }

  function reset() {
    if (!confirm('Clear all ideas and start over?')) return;
    setIdeas([newIdea()]);
  }

  function exportCsv() {
    const rows = [
      ['Name', 'Impact', 'Confidence', 'Ease', 'ICE Score'],
      ...sorted.map((i) => [i.name || 'Untitled', i.impact, i.confidence, i.ease, i.score]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ice-scores.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground w-8">#</th>
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[220px]">
                  Idea
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Impact
                  <div className="text-[10px] font-normal text-muted-foreground/70">1–10</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Confidence
                  <div className="text-[10px] font-normal text-muted-foreground/70">1–10</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Ease
                  <div className="text-[10px] font-normal text-muted-foreground/70">1–10</div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">ICE</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((idea, i) => (
                <tr
                  key={idea.id}
                  className={i < sorted.length - 1 ? 'border-b border-border/40' : ''}
                >
                  <td className="py-2 px-4 text-muted-foreground font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={idea.name}
                      onChange={(e) => update(idea.id, { name: e.target.value })}
                      placeholder="Untitled idea"
                      className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                    />
                  </td>
                  {(['impact', 'confidence', 'ease'] as const).map((field) => (
                    <td key={field} className="py-2 px-4">
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={idea[field]}
                        onChange={(e) =>
                          update(idea.id, {
                            [field]: Math.max(1, Math.min(10, Number(e.target.value))),
                          })
                        }
                        className="w-16 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                  ))}
                  <td className="py-2 px-4 text-right font-mono font-semibold text-primary">
                    {idea.score}
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => remove(idea.id)}
                      aria-label="Remove idea"
                      className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-muted-foreground">
                    No ideas yet. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add idea
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Your scores are saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}
